import isEmpty from 'lodash-es/isEmpty';
import { isAnalyticsAllowed } from './utils.js';
import * as Sentry from '@sentry/browser';

/**
 * It's ES module used to manage posthog.
 * It starts posthog initialization whenever this module is loaded.
 */

let initPromiseResolve;

// `posthog-js` (~260 KB) is loaded on demand, so it stays out of the app shell
// and out of the boot path. `instancePromise` settles once `init` has created the instance;
// it never settles when PostHog is disabled, so calls made in that case are simply dropped.
let instancePromiseResolve;
let instancePromise = new Promise(resolve => {
  instancePromiseResolve = resolve;
});

let initPromise = new Promise(resolve => {
  initPromiseResolve = resolve;
});

/**
 * Initialize PostHog.
 * @param {Object} params
 *  - @property {String} APIKey
 *  - @property {String} APIHost PostHog api endpoint host.
 *  - @property {String} userId
 *  - @property {Object} sentryConfig Sentry configuration for integration
 *    - @property {String} organization Sentry organization slug
 *    - @property {String} projectId Sentry project ID
 */
export const init = async ({APIKey, APIHost, userId = '', sentryConfig = {}}) => {
  if(!APIKey) {
    console.log('PostHog init skipped: No API key provided');
    return;
  }

  // Initialised even before consent, so the SDK sees the landing URL and records the campaign
  // that brought the visitor in — `utm_*` and the ad click ids (`gclid`, `gbraid`, `wbraid`).
  // By the time a visitor answers the cookie banner that URL is gone.
  //
  // `cookieless_mode: 'on_reject'` is what makes this safe: while consent is pending or refused
  // PostHog captures without setting a single cookie, counting the visitor through a
  // privacy-preserving server-side hash instead. Cookies only start once the visitor opts in.
  //
  // `opt_out_capturing_by_default` is required alongside it, not belt-and-braces: PostHog treats
  // a pending decision as a rejection only when this is set, and pending — the visitor who never
  // answers the banner — is most of our ad traffic.
  //
  // Requires "Cookieless server hash mode" enabled in PostHog under Project Settings > Web
  // analytics.
  const config = {
    cookieless_mode: 'on_reject',
    opt_out_capturing_by_default: true,
    api_host: `https://${APIHost}/zGFWePb85mezrA` || 'https://app.posthog.com',
    autocapture: true,
    capture_pageview: true,
    capture_pageleave: true,
    // `recordCrossOriginIframes` only works when both frames run the recorder, so the
    // website sets it too. Without it a replay of the website's embedded import shows an
    // empty box where this app is.
    session_recording: { maskAllInputs: false, recordCrossOriginIframes: true },
    secure_cookie: true,
    cross_subdomain_cookie: true,
    rate_limiting: {
      events_per_second: 200,
    },
    disable_session_recording: true,  // We manually control session recording via initSessionRecording/stopSessionRecording
    loaded: function(posthogInstance) {
      initPromiseResolve();
      if(userId) {
        posthogInstance.identify(userId);
      }
    }
  };

  console.log('Initializing PostHog with API Key:', APIKey, 'and config:', config);
  const { default: posthog } = await import('posthog-js');
  posthog.init(APIKey, config);
  instancePromiseResolve(posthog);

  // Only add Sentry integration if organization and projectId are configured
  if (sentryConfig.organization && sentryConfig.projectId) {
    Sentry.addIntegration(posthog.sentryIntegration({
      organization: sentryConfig.organization,
      projectId: sentryConfig.projectId,
      severityAllowList: ['error', 'fatal'],
      sendExceptionsToPostHog: true,
    }));
    console.log('PostHog Sentry integration enabled');
  } else {
    console.log('PostHog Sentry integration skipped: organization or projectId not configured');
  }
}

/**
 * Move PostHog onto its consented footing: persist across sessions and start sending.
 *
 * PostHog copies the properties it already holds into the new store when persistence changes, so
 * the campaign parameters captured at landing — including the ad click id — survive this switch
 * and stay attached to the visitor.
 */
export const grantConsent = async () => {
  try {
    const posthog = await instancePromise;
    posthog.opt_in_capturing();
  } catch (error) {
    console.warn('PostHog: failed to apply analytics consent', error);
  }
};

/**
 * Drop back to the unconsented footing: stop sending and keep nothing on the device.
 * Called when the visitor declines, or withdraws consent they had given.
 */
export const revokeConsent = async () => {
  try {
    const posthog = await instancePromise;
    posthog.opt_out_capturing();
  } catch (error) {
    console.warn('PostHog: failed to withdraw analytics consent', error);
  }
};

/**
 * Track PostHog event.
 * @param {String} name event name
 * @param {Object} properties event properties
 */
export const logEvent = async (name, properties = {}) => {
  // Deliberately not gated on consent. PostHog decides for itself whether to record, from the
  // consent state it holds: while that is pending or refused it captures cookielessly, storing
  // nothing on the device. Gating here would drop exactly the events this is meant to keep.
  await initPromise;
  const posthog = await instancePromise;
  posthog.capture(name, properties);
}

/**
 * Reset the PostHog session. Clears session ID and distinct ID but preserves device ID.
 * Should be called on logout to prevent the old user's session from leaking into the next user's events.
 */
export const reset = async () => {
  if(!isAnalyticsAllowed()) {
    return;
  }

  await initPromise;
  const posthog = await instancePromise;
  posthog.reset();
};

/**
 * Set user properties.
 * @param {String} userId The user ID to set
 * @param {Object} properties The properties to set for the user
 * @param {Boolean} resetSession Whether to reset the session
 */
export const setUserProperties = async(userId, properties, resetSession = false, setOnceProperties = {}) => {
  // Not gated on consent, for the same reason `logEvent` is not: PostHog is running cookielessly
  // from the landing page, and until the visitor is identified their events sit on an anonymous
  // person that never joins their account. Calling identify on login hands PostHog the link, and
  // it merges the earlier cookieless person into the user — verified on test1, with the pre-login
  // events landing on the user's person while consent was still pending.
  await initPromise;
  const posthog = await instancePromise;
  if(resetSession || userId === null) {
    reset();
  }

  if(userId) {
    posthog.identify(userId);
  }

  if(!isEmpty(properties) || !isEmpty(setOnceProperties)) {
    // Second arg is the $set_once payload — PostHog server only writes if property
    // doesn't already exist on the user (first-touch semantics).
    posthog.setPersonProperties(properties, setOnceProperties);
  }
}

/**
 * Start session recording for PostHog
 * @param {String} APIKey - The API key for PostHog
 * @param {Object} options - Additional configuration options for session recording
 * @returns {Boolean} True if session recording was started, false otherwise
 */
export const startSessionRecording = async(APIKey, options = {}) => {
  if (!APIKey) {
    console.log('PostHog session recording cannot start: missing API key');
    return false;
  }

  if (!isAnalyticsAllowed()) {
    console.log('PostHog session recording cannot start: Analytics cookie consent not given');
    return false;
  }

  try {
    await initPromise;
    const posthog = await instancePromise;
    posthog.startSessionRecording();
    console.log('PostHog session recording started successfully');
    return true;
  } catch (error) {
    console.error('Failed to start PostHog session recording:', error);
    return false;
  }
}

/**
 * Stop session recording
 */
export const stopSessionRecording = async() => {
  if (!isAnalyticsAllowed()) {
    return;
  }

  try {
    await initPromise;
    const posthog = await instancePromise;
    posthog.stopSessionRecording();
    console.log('PostHog session recording stopped successfully');
  } catch (error) {
    console.error('Failed to stop PostHog session recording:', error);
  }
}
